import { useSearchUpdate } from 'hooks/useSearch';
import { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaXmark } from 'react-icons/fa6';

// Temporary JSDoc typing until SearchBar is migrated to TypeScript.

/**
 * @typedef {Object} SearchBarProps
 * @property {string} [className]
 * @property {string} [inputClassName]
 * @property {boolean} [disabled]
 * @property {string} [placeholder]
 * @property {string} [ariaLabel]
 * @property {boolean} [includeClear]
 * @property {string} [value]
 * @property {(value: string) => void} [onChange]
 */

/**
 * @param {SearchBarProps} props
 */
export default function SearchBar({
  className,
  inputClassName,
  disabled,
  placeholder = 'Search',
  ariaLabel,
  includeClear = true,
  value,
  onChange,
}) {
  const [search, updateSearch] = useSearchUpdate();
  const isControlled = value !== undefined;
  const searchValue = isControlled ? value : search;
  const [inputValue, setInputValue] = useState(searchValue || '');
  const timeoutRef = useRef(null);

  const updateSearchValue = (nextValue) => {
    setInputValue(nextValue);

    if (isControlled) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        onChange?.(nextValue);
      }, 400);
    } else {
      updateSearch(nextValue);
    }
  };

  // Ensures that if the searchValue changed somehow it will still be correct
  useEffect(() => {
    setInputValue(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Form.Group
      className={`fusion-search-control d-flex w-100 rounded-3 border border-1 border-secondary bg-primary ${className}`}
    >
      <Form.Control
        className={`border-0 rounded-0 bg-transparent ${inputClassName}`}
        disabled={disabled}
        value={inputValue}
        onChange={(event) => updateSearchValue(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      {includeClear && !disabled && (
        <Button
          className="bg-transparent border-0"
          onClick={() => updateSearchValue('')}
          variant="secondary"
          aria-label="Clear search"
        >
          <FaXmark aria-hidden="true" />
        </Button>
      )}
    </Form.Group>
  );
}
